import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus } from 'lucide-react';
import CustomerSelect from "../ui/CustomerSelect";

const AddProjectDialog = ({ isDialogOpen, setIsDialogOpen, newProject, setNewProject, handleAddProject, creating, customers, designers, subcontractors }) => (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
            <Button 
                className="bg-brand-600 hover:bg-brand-700"
                onClick={() => setIsDialogOpen(true)}
            >
                <Plus className="mr-2 h-4 w-4" />
                New Project
            </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
            <DialogHeader>
                <DialogTitle>Create New Project</DialogTitle>
                <DialogDescription>
                    Start a new landscaping project with customer and timeline details.
                </DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
                {/* Basic Information Section */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2 space-y-2">
                            <Label htmlFor="name">Project Name *</Label>
                            <Input
                                id="name"
                                value={newProject.name}
                                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                                placeholder="Garden Redesign Project"
                            />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <Label htmlFor="customer">Customer *</Label>
                            <CustomerSelect 
                                customerId={newProject.customer_id}
                                setCustomerId={(id) => setNewProject({ ...newProject, customer_id: id })}
                                customers={customers}
                                isDisabled={creating} 
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="projectType">Project Type *</Label>
                            <Select value={newProject.project_type} onValueChange={(value) => setNewProject({ ...newProject, project_type: value })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="design_only">Design Only</SelectItem>
                                    <SelectItem value="construction">Construction</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="priority">Priority</Label>
                            <Select value={newProject.priority} onValueChange={(value) => setNewProject({ ...newProject, priority: value })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select priority" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">Low</SelectItem>
                                    <SelectItem value="medium">Medium</SelectItem>
                                    <SelectItem value="high">High</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="company">Company</Label>
                            <Select value={newProject.company || ""} onValueChange={(value) => setNewProject({ ...newProject, company: value })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select company" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Landscaping">Landscaping</SelectItem>
                                    <SelectItem value="Exteriors">Exteriors</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Location Section */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Jobsite Location</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2 space-y-2">
                            <Label htmlFor="address">Street Address</Label>
                            <Input
                                id="address"
                                value={newProject.jobsite_address}
                                onChange={(e) => setNewProject({ ...newProject, jobsite_address: e.target.value })}
                                placeholder="123 Main St"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="city">City</Label>
                            <Input
                                id="city"
                                value={newProject.city || ""}
                                onChange={(e) => setNewProject({ ...newProject, city: e.target.value })}
                                placeholder="City"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="zip">Zip Code</Label>
                            <Input
                                id="zip"
                                value={newProject.zip || ""}
                                onChange={(e) => setNewProject({ ...newProject, zip: e.target.value })}
                                placeholder="12345"
                            />
                        </div>
                    </div>
                </div>

                {/* Timeline Section */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Project Timeline</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="contractSignDate">Contract Sign Date</Label>
                            <Input
                                id="contractSignDate"
                                type="date"
                                value={newProject.contract_sign_date}
                                onChange={(e) => setNewProject({ ...newProject, contract_sign_date: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="startDate">Start Date</Label>
                            <Input
                                id="startDate"
                                type="date"
                                value={newProject.start_date}
                                onChange={(e) => setNewProject({ ...newProject, start_date: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="endDate">Est. End Date</Label>
                            <Input
                                id="endDate"
                                type="date"
                                value={newProject.end_date}
                                onChange={(e) => setNewProject({ ...newProject, end_date: e.target.value })}
                            />
                        </div>
                    </div>
                </div>

                {/* Financial Section */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Financial Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="contractPrice">{newProject.project_type === "design_only" ? "Design Fee *" : "Contract Price *"}</Label>
                            <Input
                                id="contractPrice"
                                type="number"
                                value={newProject.contract_price}
                                onChange={(e) => setNewProject({ ...newProject, contract_price: e.target.value })}
                                placeholder={newProject.project_type === "design_only" ? "5000" : "75000"}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="crewPayrollEstimate">Crew Payroll Estimate</Label>
                            <Input
                                id="crewPayrollEstimate"
                                type="number"
                                value={newProject.crew_payroll_estimate}
                                onChange={(e) => setNewProject({ ...newProject, crew_payroll_estimate: e.target.value })}
                                placeholder="15000"
                            />
                        </div>
                        {newProject.project_type === "design_only" && (
                            <div className="space-y-2">
                                <Label htmlFor="estimatedBudget">Estimated Construction Budget</Label>
                                <Input
                                    id="estimatedBudget"
                                    type="number"
                                    value={newProject.estimated_construction_budget}
                                    onChange={(e) => setNewProject({ ...newProject, estimated_construction_budget: e.target.value })}
                                    placeholder="150000"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Team Assignment removed — designers, crews, subs are all assigned from the project view dialog */}

                {/* User Assignment removed — assigned from the project view dialog */}

                {/* Additional Details Section */}
                <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Additional Details</h3>
                    <div className="space-y-2">
                        <Label htmlFor="description">Project Description</Label>
                        <Textarea
                            id="description"
                            value={newProject.description}
                            onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                            placeholder="Describe the landscaping project..."
                            rows={3}
                        />
                    </div>
                </div>
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                </Button>
                <Button 
                    onClick={handleAddProject}
                    disabled={creating || !newProject.name || !newProject.customer_id || !newProject.contract_price}
                    className="bg-brand-600 hover:bg-brand-700"
                >
                    {creating ? "Creating..." : "Create Project"}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
);

export default AddProjectDialog;